'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/i18n/client';
import { sample, shuffle, SENTENCES } from '@/content/games';
import { playCorrect, playLose, playTap, playWin, playWrong } from '@/lib/sound';
import { Hearts } from './Hearts';
import type { GameFinish } from './WordMatch';

const DEFAULT_ROUNDS = 5;
/** Wrong attempts allowed across the whole round before it ends. */
const MAX_MISTAKES = 3;

function toWords(sentence: string): string[] {
  return sentence.replace(/\s+/g, ' ').trim().split(' ');
}

/** Reorder scrambled word chips into the correct English sentence. */
export function SentenceBuilder({
  onFinish,
  bank = SENTENCES,
}: {
  onFinish: (result: GameFinish) => void;
  /** Optional sentence bank (e.g. a lesson's examples); defaults to the global bank. */
  bank?: string[];
}) {
  const t = useT();
  const [round, setRound] = useState(0);
  const ROUNDS = Math.min(DEFAULT_ROUNDS, bank.length) || DEFAULT_ROUNDS;
  const sentences = useMemo(() => sample(bank, ROUNDS), [round, bank, ROUNDS]);
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState<number[]>([]);
  const [checked, setChecked] = useState<boolean | null>(null);
  const [mistakes, setMistakes] = useState(0);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const correctRef = useRef(0);
  const [correct, setCorrect] = useState(0);

  const target = sentences[index] ?? '';
  const chips = useMemo(
    () => shuffle(toWords(target).map((word, id) => ({ id, word }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [index, round],
  );

  useEffect(() => {
    setAnswer([]);
    setChecked(null);
  }, [index, round]);

  const wordFor = (id: number) => chips.find((chip) => chip.id === id)?.word ?? '';
  const tray = chips.filter((chip) => !answer.includes(chip.id));
  const full = answer.length === chips.length;

  function addChip(id: number) {
    if (checked !== null) return;
    playTap();
    setAnswer((current) => [...current, id]);
  }
  function removeChip(position: number) {
    if (checked !== null) return;
    setAnswer((current) => current.filter((_, i) => i !== position));
  }

  function check() {
    const guess = answer.map(wordFor).join(' ');
    const ok = guess === target;
    if (ok) {
      setChecked(true);
      correctRef.current += 1;
      setCorrect((n) => n + 1);
      playCorrect();
      return;
    }
    const nextMistakes = mistakes + 1;
    setMistakes(nextMistakes);
    if (nextMistakes >= MAX_MISTAKES) {
      setChecked(false);
      setDone(true);
      setFailed(true);
      playLose();
      onFinish({ correct: correctRef.current, total: ROUNDS, score: correctRef.current * 100 });
      return;
    }
    // Wrong but hearts remain: flash red, don't reveal, let them rearrange.
    playWrong();
    setChecked(false);
    window.setTimeout(() => {
      setChecked(null);
      setAnswer([]);
    }, 900);
  }

  function advance() {
    if (index + 1 < sentences.length) {
      setIndex(index + 1);
    } else {
      setDone(true);
      playWin();
      onFinish({ correct: correctRef.current, total: ROUNDS, score: correctRef.current * 100 });
    }
  }

  function playAgain() {
    correctRef.current = 0;
    setCorrect(0);
    setIndex(0);
    setMistakes(0);
    setDone(false);
    setFailed(false);
    setRound((r) => r + 1);
  }

  if (done) {
    return (
      <div className="vn-game2 vn-game2--center">
        <p className="vn-game2__result-title">
          {failed ? t('games.outOfHearts') : t('games.roundResult', { correct, total: ROUNDS })}
        </p>
        <button type="button" className="vn-btn vn-btn--primary" onClick={playAgain}>
          {t('games.playAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="vn-game2">
      <div className="vn-game2__topbar">
        <p className="vn-game2__status">{t('games.position', { current: index + 1, total: ROUNDS })}</p>
        <Hearts mistakes={mistakes} max={MAX_MISTAKES} />
      </div>
      <p className="vn-build__hint">{t('games.tapToBuild')}</p>

      <div className={`vn-build__answer${checked === true ? ' is-correct' : ''}${checked === false ? ' is-wrong' : ''}`}>
        {answer.length === 0 ? <span className="vn-build__placeholder">…</span> : null}
        {answer.map((id, position) => (
          <button key={position} type="button" className="vn-build__chip" onClick={() => removeChip(position)}>
            {wordFor(id)}
          </button>
        ))}
      </div>

      <div className="vn-build__tray">
        {tray.map((chip) => (
          <button key={chip.id} type="button" className="vn-build__chip vn-build__chip--tray" onClick={() => addChip(chip.id)}>
            {chip.word}
          </button>
        ))}
      </div>

      {checked === false ? <p className="vn-build__solution">{t('games.builderTryAgain')}</p> : null}
      {checked === true ? <p className="vn-build__ok">{t('games.builderCorrect')}</p> : null}

      <div className="vn-game2__actions">
        {checked === true ? (
          <button type="button" className="vn-btn vn-btn--primary" onClick={advance}>
            {index + 1 < sentences.length ? t('games.next') : t('games.finish')}
          </button>
        ) : (
          <button
            type="button"
            className="vn-btn vn-btn--primary"
            disabled={!full || checked === false}
            onClick={check}
          >
            {t('games.check')}
          </button>
        )}
      </div>
    </div>
  );
}
